'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineGlobe } from '@qr-front/icons/line-globe'
import {
	Collapse,
	CollapseContent,
	CollapseTrigger,
} from '@qr-front/ui/components/Collapse'
import { ScrollBarWrapper } from '@qr-front/ui/components/ScrollBarWrapper'
import { cn } from '@qr-front/ui/utils'
import { useParams } from 'next/navigation'
import { startTransition, useEffect, useMemo, useRef, useState } from 'react'
import { LanguageType } from 'services/languages/languageTypes'

import { DEFAULT_LOCALE } from '@/constants'
import { usePathname, useRouter } from '@/i18n-routing'

export const LanguageSelectMobile = ({
	languageList,
	labelClasses,
	chevronClasses,
	locale,
	triggerCls,
}: {
	languageList: LanguageType[]
	labelClasses?: string
	chevronClasses?: string
	locale: string
	triggerCls?: string
}) => {
	const router = useRouter()
	const pathname = usePathname()
	const params = useParams()
	const [isOpen, setIsOpen] = useState(false)

	const selectedRef = useRef<HTMLLIElement | null>(null)
	const [selectedLocale, setSelectedLocale] = useState(locale)

	useEffect(() => {
		setSelectedLocale(locale)
	}, [locale])

	const handleLanguageClick = (value: string) => {
		if (value === DEFAULT_LOCALE) {
			document.cookie = `lang=${DEFAULT_LOCALE}; max-age=315360000; domain=${window.location.hostname}; path=/`
		}
		setSelectedLocale(value)
		startTransition(() => {
			router.replace(
				// @ts-expect-error -- TypeScript will validate that only known `params`
				// are used in combination with a given `pathname`. Since the two will
				// always match for the current route, we can skip runtime checks.
				{ pathname, params },
				{ locale: value, scroll: false },
			)
		})
	}

	const sortedLanguages = useMemo(() => {
		return languageList.slice().sort((a, b) => a.name.localeCompare(b.name))
	}, [languageList])

	const labelToShow = useMemo(() => {
		return languageList.find((lang) => lang.code === selectedLocale)
	}, [languageList, selectedLocale])

	return (
		<Collapse open={isOpen} onOpenChange={setIsOpen}>
			<CollapseTrigger
				className={cn(
					'w-full text-black outline-none [&[data-state=open]>svg]:rotate-180',
					triggerCls,
				)}
			>
				<div className="flex items-center gap-2">
					<LineGlobe aria-label="Globe icon" />
					<span className={labelClasses}>{labelToShow?.name}</span>
				</div>
				<LineChevronDownSmall
					className={cn(chevronClasses, 'transition-transform duration-200')}
					aria-label="Arrow down icon"
				/>
			</CollapseTrigger>
			<CollapseContent className="mt-4 h-49 max-h-49 rounded-8 border border-border-grey-50 p-2">
				<ScrollBarWrapper
					scrollbarMinSize={18}
					childrenClassNames="mr-3"
					className="h-full"
				>
					<ul className="flex flex-col gap-1">
						{sortedLanguages.map((lang) => (
							<li
								key={lang.code}
								ref={lang.code === selectedLocale ? selectedRef : null}
								className={cn(
									'type-body-small flex h-8 cursor-pointer items-center rounded-8 px-4 py-2 outline-none transition-colors hover:bg-purple-8 hover:text-blue',
									lang.code === selectedLocale
										? 'bg-purple-8 text-blue'
										: 'bg-white text-black',
								)}
								onClick={() => handleLanguageClick(lang.code)}
							>
								{lang.name}
							</li>
						))}
					</ul>
				</ScrollBarWrapper>
			</CollapseContent>
		</Collapse>
	)
}
